import { Logger } from './logger';
import { LoggerNames } from './constants';

export class NetworkError extends Error {
  constructor(
    message: string,
    public readonly status: number,
    public readonly url: string,
  ) {
    super(`A network error occurred with status ${status}: \n\t${message}\n\tURL: ${url}`);
  }
}

type NarrowedResponse = Pick<Response, 'ok' | 'json' | 'status' | 'statusText' | 'url'>;

/**
 * Checks the status of a fetch response and throws an error if the response is not ok.
 * If the response is ok, the response is returned. Otherwise, a NetworkError is thrown
 *
 * @param response - The Fetch response to check
 * @throws {NetworkError} - If the response is not ok
 */
export async function checkFetchStatus(response: NarrowedResponse): Promise<NarrowedResponse> {
  const logger = Logger.createLogger(LoggerNames.FETCH);
  if (response.ok) {
    logger.debug(`Received a 2xx response from the server`);
    return response;
  } else {
    logger.debug(`Received a non-2xx response from the server`, response.status, response.statusText);
    throw new NetworkError(response.statusText, response.status, response.url);
  }
}

/**
 * Attempts to parse a fetch response as JSON.
 * Will first check the status of the response and throw an error if the response is not ok.
 * @param response - The Fetch response to parse
 * @throws {NetworkError} - If the response is not ok
 */
export function parseResponseAsJson(response: NarrowedResponse) {
  return checkFetchStatus(response).then((res) => res.json());
}
